import { AvatarStack, Button, Tooltip } from '@civilgrid/ui/components';
import { useNavigate } from '@tanstack/react-router';
import { isAxiosError } from 'axios';
import { useEffect } from 'react';
import { useSnapshot } from 'valtio';
import { joinOrgByDomain } from '~/api/auth2';
import authStore from '~/authStore';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import Notify from '~/components2/Notify';
import rootStore from '../../store';
import type { OrgSnippet } from './common';
import PageLayout from './PageLayout';
import RegisterContact from './RegisterContact';

export default function JoinOrganization() {
  const { me } = useSnapshot(rootStore);

  useEffect(() => {
    if (me && me.availableOrgs.length > 0) {
      capturePosthogEvent(POSTHOG_EVENTS.organizationJoinViewed, {
        available_orgs: me.availableOrgs.length,
        total_members_across_orgs: me.availableOrgs.reduce((acc, org) => acc + org.numMembers, 0),
      });
    }
  }, [me]);

  // redirect to login if me is undefined or not part of this page
  if (me && me.availableOrgs.length > 0) {
    return (
      <PageLayout data-testid="join-org-page" rightSideContent={<RegisterContact />}>
        <div className="flex h-5/6 w-full flex-col justify-between">
          <div className="flex h-full flex-col items-start justify-start space-y-2 px-4">
            <div className="text-lg font-bold">Join your teammates</div>
            <div className="flex flex-col justify-start text-xs text-slate-500">
              <p className="break-words">
                {`Your teammates are using ${me.availableOrgs.length} ${
                  me.availableOrgs.length === 1 ? 'organization' : 'organizations'
                }. Join them to start collaborating.`}
              </p>
            </div>
            <div className="flex w-full flex-col overflow-y-auto">
              <div className="flex flex-col space-y-4">
                {me.availableOrgs.map((org) => (
                  <div key={org.id} className="max-h-3/4">
                    <OrgCard org={org} />
                  </div>
                ))}
              </div>
            </div>
            <div className="pt-2 text-xs text-slate-500">
              If you don't see your team here, please{' '}
              <a href="mailto:support@civilgrid.com" className="text-brand underline">
                contact support
              </a>
              .
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  // clean this up
  return <div>Error...</div>;
}

type Props = {
  org: OrgSnippet;
};

const OrgCard = ({ org }: Props) => {
  let memberDisplayString = org.memberString;

  const navigate = useNavigate();

  if (memberDisplayString.length === 0) {
    memberDisplayString = 'No Known Members';
  }

  const handleJoinOrg = async () => {
    try {
      const { status } = await joinOrgByDomain(org.id);

      // if user was added to the org, retry bootstrap and push to home.
      // Cookie mode has no token, so bootstrap with '' and rely on the HttpOnly cookie.
      if (status === 200) {
        await rootStore.bootstrap(authStore.token ?? '');
        navigate({ to: '/verification' });
        capturePosthogEvent(POSTHOG_EVENTS.organizationJoined, {
          organization_id: org.id,
          organization_name: org.name,
          num_members: org.numMembers,
        });
      }
    } catch (e) {
      console.error(e);
      Notify.error({
        title: '⚠️',
        message: (isAxiosError(e) && e.response?.data?.error) || 'Could not join this organization.',
      });
    }
  };

  return (
    <div className="flex flex-col rounded-sm bg-primary-light/30 p-2">
      <div className="flex items-center justify-between ">
        <div>
          <div className="break-words text-base font-bold">{org.name}</div>
          <div className="text-xs text-slate-500">{org.numMembers} members</div>
        </div>
        <div className="ml-4">
          <Button variant="secondary" onClick={() => handleJoinOrg()} data-testid={`join-org-join-button-${org.id}`}>
            Join
          </Button>
        </div>
      </div>

      <div className="flex flex-row items-center space-x-2 pt-4">
        <Tooltip
          aria-label="All members"
          data-testid={`join-org-members-${org.id}`}
          className="bg-white"
          side="bottom"
          align="start"
          contentChildren={
            <div className="flex max-h-60 w-96 cursor-pointer flex-col overflow-visible overflow-y-auto p-2 text-black">
              <div className="pb-2 font-bold">All Members</div>
              {org.activeUsers.map((user) => {
                return <div key={user.id}>{user.name}</div>;
              })}
            </div>
          }
        >
          <AvatarStack users={org.activeUsers} total={org.numMembers} />
        </Tooltip>
        <div className="text-xs">{memberDisplayString}</div>
      </div>
    </div>
  );
};
